import React, { useEffect, useMemo } from 'react';

import _ from 'lodash';
import moment from 'moment';

import Table, { IndeterminateCheckbox } from 'components/Table';
import { FORMAT_TIME } from 'constants/index';
import { TEXT_SURVEY_LIST } from 'constants/string/texts';
import { convertStatusSurvey, getSurveyKindLabel } from 'utils/helper';

const SurveyListTable = ({ selectedRows, setSelectedRows, data, setSort, sort, page }) => {
  const handleRowSelect = (rowSelect) => {
    try {
      setSelectedRows((prevSelected) => {
        const pageSelectedExists = _.findIndex(selectedRows, { page: page });
        const selectedRowWithPage = selectedRows.concat();

        if (pageSelectedExists === -1) {
          const totalSelected = _.flatMap(selectedRows, 'selected').length;
          if (totalSelected === 10) {
            return [...prevSelected];
          }

          return [
            ...prevSelected,
            {
              page: page,
              selected: [rowSelect?.original?.id],
            },
          ];
        }

        const rowIsSelected = selectedRowWithPage[pageSelectedExists]?.selected?.find((row) => row === rowSelect?.original?.id);

        if (rowIsSelected) {
          const selectedRemovedRow = selectedRowWithPage[pageSelectedExists].selected.filter((row) => row !== rowSelect.original.id);
          selectedRowWithPage.splice(pageSelectedExists, 1);
          selectedRowWithPage.push({
            page: page,
            selected: selectedRemovedRow,
          });
          return selectedRowWithPage;
        }

        const rowSelected = _.cloneDeep(selectedRowWithPage[pageSelectedExists].selected);
        selectedRowWithPage.splice(pageSelectedExists, 1);

        if (_.flatMap(prevSelected, 'selected').length < 10) {
          rowSelected.push(rowSelect.original.id);
        }
        selectedRowWithPage.push({
          page: page,
          selected: rowSelected,
        });
        return selectedRowWithPage;
      });
      rowSelect.getToggleSelectedHandler();
    } catch {
      // Handle error select
    }
  };

  const handleSelectAll = (table) => {
    try {
      setSelectedRows((prevState) => {
        const dataRows = table.getRowModel().rows;
        const pageSelectedExists = _.findIndex(selectedRows, { page: page });
        const selectedRowWithPage = selectedRows.concat();

        if (pageSelectedExists === -1) {
          const currentTotal = _.flatMap(selectedRowWithPage, 'selected').length;

          if (currentTotal === 10) {
            return prevState;
          }

          let count = 0;
          const dataRowPage = dataRows?.map((row) => {
            if (currentTotal + count < 10) {
              count += 1;
              return row?.original?.id;
            }
          });

          return [
            ...prevState,
            {
              page: page,
              selected: _.filter(dataRowPage, (item) => item !== undefined),
            },
          ];
        }

        if (selectedRowWithPage[pageSelectedExists]?.selected?.length === dataRows?.length) {
          selectedRowWithPage.splice(pageSelectedExists, 1);
          return selectedRowWithPage;
        }

        const oldData = _.cloneDeep(selectedRowWithPage[pageSelectedExists]?.selected);
        selectedRowWithPage.splice(pageSelectedExists, 1);

        const currentTotal = _.flatMap(prevState, 'selected').length;

        let count = 0;
        const dataRowPage = dataRows?.map((row) => {
          if (currentTotal + count < 10 && !oldData.includes(row?.original?.id)) {
            count += 1;
            return row?.original?.id;
          }
        });

        selectedRowWithPage.push({
          page: page,
          selected: [...oldData, ..._.filter(dataRowPage, (item) => item !== undefined)],
        });

        return selectedRowWithPage;
      });
    } catch {
      // Handle error select all
    }
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: '-',
        header: ({ table }) => (
          <div className={'flex justify-center items-center w-4'}>
            <IndeterminateCheckbox
              className={'m-auto'}
              {...{
                checked: _.find(selectedRows, { page: page })?.selected?.length === table.getRowModel().rows?.length && data?.length > 0,
                indeterminate: false,
                onChange: () => handleSelectAll(table),
              }}
            />
          </div>
        ),
        size: 48,
        enableSorting: false,
        cell: ({ row }) => {
          const total = _.flatMap(selectedRows, 'selected').length;
          const checked = _.find(selectedRows, { page: page })?.selected?.some((item) => item === row?.original?.id);
          return (
            <div className={`flex justify-center ${total >= 10 && !checked ? 'opacity-50 !cursor-not-allowed' : ''}`}>
              <IndeterminateCheckbox
                className={'m-auto'}
                {...{
                  checked: checked,
                  indeterminate: false,
                  onChange: () => handleRowSelect(row),
                }}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'title',
        header: ({ table }) => {
          setSort(table.getState().sorting);
          return <span>{TEXT_SURVEY_LIST.CONSULTATION_NAME}</span>;
        },
        cell: ({ row, getValue }) => {
          const type_survey_name = getSurveyKindLabel(row.original);
          return (
            <div className="flex-between-center text-sm gap-5">
              <span className="break-all">
                {getValue()} {type_survey_name && `(${type_survey_name})`}
              </span>
              <span
                className={`text-xs rounded-sm flex-middle-center border border-solid min-w-13 min-h-5.75 ${convertStatusSurvey(row?.original?.status)?.class}`}
              >
                {convertStatusSurvey(row.original.status).name}
              </span>
            </div>
          );
        },
        size: 360,
        sortingFns: () => {},
      },
      {
        accessorKey: 'updated_at',
        size: 144,
        header: () => <span>{TEXT_SURVEY_LIST.EDITING_DATE}</span>,
        cell: ({ row }) => <span>{moment(row.original.updated_at).format(FORMAT_TIME.DATE)}</span>,
        sortingFns: () => {},
      },
    ],
    [selectedRows, page]
  );

  useEffect(() => {
    setSelectedRows([]);
  }, [sort]);

  return (
    <div className="py-3">
      <div className="download-table px-3">
        <Table columns={columns} data={data} isShowNoData={false} />
      </div>
    </div>
  );
};

export default React.memo(SurveyListTable);
